import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { FilterOptions, PackingFilters } from './packingApi';
import { input } from './packingStyles';

interface Props {
  value: PackingFilters;
  options: FilterOptions | null;
  onChange: (next: PackingFilters) => void;
}

const label: React.CSSProperties = { fontSize: 13, color: '#475569', fontWeight: 600, flex: '1 1 160px', maxWidth: 240 };

/**
 * Site / line / week lists of the packing station. Choosing a site narrows the lines to that site;
 * a line from another site is dropped when the site changes.
 */
const PackingFiltersBar: React.FC<Props> = ({ value, options, onChange }) => {
  const { t } = useLanguage();
  const lines = (options?.lines ?? []).filter((l) => !value.location || l.location === value.location);
  // Keep a chosen value selectable while the options are loading or after it left the list.
  const weeks = options?.weeks ?? [];
  const locations = options?.locations ?? [];

  const setLocation = (location: string) => {
    const keepLine = !location || (options?.lines ?? []).some((l) => String(l.id) === value.lineId && l.location === location);
    onChange({ ...value, location, lineId: keepLine ? value.lineId : '' });
  };

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={label}>
        {t('packing.filters.location')}
        <select value={value.location} onChange={(e) => setLocation(e.target.value)} style={{ ...input, marginTop: 4 }}>
          <option value="">{t('packing.filters.allLocations')}</option>
          {value.location && !locations.includes(value.location) && <option value={value.location}>{value.location}</option>}
          {locations.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
      </label>
      <label style={label}>
        {t('packing.filters.line')}
        <select value={value.lineId} onChange={(e) => onChange({ ...value, lineId: e.target.value })} style={{ ...input, marginTop: 4 }}>
          <option value="">{t('packing.filters.allLines')}</option>
          {value.lineId && !lines.some((l) => String(l.id) === value.lineId) && (
            <option value={value.lineId}>{options?.lines.find((l) => String(l.id) === value.lineId)?.name ?? '…'}</option>
          )}
          {lines.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
        </select>
      </label>
      <label style={label}>
        {t('packing.filters.week')}
        <select value={value.week} onChange={(e) => onChange({ ...value, week: e.target.value })} style={{ ...input, marginTop: 4 }}>
          <option value="">{t('packing.filters.allWeeks')}</option>
          {value.week && !weeks.includes(value.week) && <option value={value.week}>{value.week}</option>}
          {weeks.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </label>
    </div>
  );
};

export default PackingFiltersBar;
